import { useCallback, useEffect, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconSettings,
} from '@tabler/icons-react'
import { useArchbaseRemoteServiceApi } from '@archbase/data'
import { API_TYPE } from '@ioc/IOCTypes'
import type { DoclenzKeyService } from '@services/DoclenzKeyService'
import { TenantSettingsModal } from './TenantSettingsModal'
import type { ApiKeyEntryDto } from '@domain/apiKey/ApiKeyDto'
import { AVAILABLE_FEATURES } from '../../AppConstants'

export function KeysView() {
  const keyApi = useArchbaseRemoteServiceApi<DoclenzKeyService>(API_TYPE.DoclenzKey)
  const [entries, setEntries] = useState<ApiKeyEntryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  /** Chave cujos overrides de tenant estão abertos para edição (ADR-007 D-5). */
  const [tenantSettingsFor, setTenantSettingsFor] = useState<ApiKeyEntryDto | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await keyApi.findAll()
      setEntries(response.keys || [])
    } catch (err: any) {
      setError(err?.message || 'Não foi possível carregar as chaves.')
    } finally {
      setLoading(false)
    }
  }, [keyApi])

  useEffect(() => {
    load()
  }, [load])

  const handleRevoke = (entry: ApiKeyEntryDto) => {
    modals.openConfirmModal({
      title: 'Revogar chave',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            A chave <Code>{entry.api_key}</Code> do tenant <b>{entry.record.tenant_id}</b> deixará de
            funcionar imediatamente.
          </Text>
          <Text size="sm" c="red">
            Não há como desfazer. Quem estiver usando esta chave passa a receber 401.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Revogar', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          // Pelo keyId: a chave em claro não existe mais em lugar nenhum depois da criação.
          await keyApi.revoke(entry.key_id)
          notifications.show({ message: 'Chave revogada.', color: 'green' })
          load()
        } catch (err: any) {
          notifications.show({
            title: 'Falha ao revogar',
            message: err?.message || 'Erro desconhecido',
            color: 'red',
          })
        }
      },
    })
  }

  return (
    /*
      O contêiner de abas do Archbase tem altura definida e não rola: sem isto a lista cresce
      além do fundo da tela e as últimas linhas simplesmente somem, sem barra nenhuma. A rolagem
      tem de ser da view, não da página.

      minHeight 0 não é decoração: num filho de flex o padrão é `auto`, que impede o encolhimento
      e faz o overflow vazar em vez de rolar.
    */
    <Stack
      p="md"
      gap="lg"
      style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}
    >
      <Group justify="space-between">
        <Title order={3}>Chaves de API</Title>
        <Group>
          <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load}>
            Atualizar
          </Button>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
            Nova chave
          </Button>
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <Card withBorder radius="md" p="md">
        {loading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            {/* Sete colunas, incluindo lista de features — a mais larga do admin. Sem ScrollContainer a tabela empurra o layout
              para fora da viewport em vez de rolar dentro do card. */}
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Chave</Table.Th>
                <Table.Th>Tenant</Table.Th>
                <Table.Th>Produto</Table.Th>
                <Table.Th ta="right">req/min</Table.Th>
                <Table.Th ta="right">UPs/mês</Table.Th>
                <Table.Th>Features</Table.Th>
                <Table.Th>Situação</Table.Th>
                <Table.Th w={60} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      Nenhuma chave cadastrada.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {entries.map((entry) => (
                <Table.Tr key={entry.key_id}>
                  <Table.Td>
                    <Tooltip label="A chave em claro só aparece na criação — esta é a versão mascarada">
                      <Code>{entry.api_key}</Code>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500}>{entry.record.tenant_id}</Text>
                  </Table.Td>
                  <Table.Td>{entry.record.product}</Table.Td>
                  <Table.Td ta="right">{entry.record.requests_per_minute}</Table.Td>
                  <Table.Td ta="right">
                    {entry.record.ups_per_month > 0 ? entry.record.ups_per_month.toLocaleString('pt-BR') : '—'}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {(entry.record.features || []).map((feature) => (
                        <Badge key={feature} size="sm" variant="light">
                          {feature}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={entry.record.status === 'active' ? 'green' : 'orange'} variant="light">
                      {entry.record.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Configurações do tenant (LLM, destino do conteúdo)">
                        <ActionIcon variant="subtle" onClick={() => setTenantSettingsFor(entry)}>
                          <IconSettings size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Revogar">
                        <ActionIcon color="red" variant="subtle" onClick={() => handleRevoke(entry)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <CreateKeyModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(apiKey) => {
          setCreateOpen(false)
          setCreatedKey(apiKey)
          load()
        }}
      />

      <CreatedKeyModal apiKey={createdKey} onClose={() => setCreatedKey(null)} />

      <TenantSettingsModal
        entry={tenantSettingsFor}
        onClose={() => setTenantSettingsFor(null)}
        onSaved={() => {
          setTenantSettingsFor(null)
          load()
        }}
        save={async (keyId, settings) => {
          // O PUT substitui o registro inteiro, então parte-se do que já está gravado e só os
          // overrides mudam — senão editar a chave de LLM zeraria cota, features e webhook.
          const current = entries.find((e) => e.key_id === keyId)
          if (!current) return
          await keyApi.update(keyId, { ...current.record, settings })
        }}
      />
    </Stack>
  )
}

function CreateKeyModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean
  onClose: () => void
  onCreated: (apiKey: string) => void
}) {
  const keyApi = useArchbaseRemoteServiceApi<DoclenzKeyService>(API_TYPE.DoclenzKey)
  const [tenantId, setTenantId] = useState('')
  const [product, setProduct] = useState('')
  const [requestsPerMinute, setRequestsPerMinute] = useState<number>(60)
  const [upsPerDay, setUpsPerDay] = useState<number>(10000)
  const [upsPerMonth, setUpsPerMonth] = useState<number>(0)
  const [features, setFeatures] = useState<string[]>(['ocr'])
  const [webhookUrl, setWebhookUrl] = useState('')
  const [saving, setSaving] = useState(false)

  const handleCreate = async () => {
    if (!tenantId.trim()) return
    setSaving(true)
    try {
      const created = await keyApi.create({
        tenant_id: tenantId.trim(),
        product: product.trim() || tenantId.trim(),
        requests_per_minute: requestsPerMinute,
        ups_per_day: upsPerDay,
        ups_per_month: upsPerMonth,
        features,
        webhook_url: webhookUrl.trim() || null,
      })
      onCreated(created.api_key)
    } catch (err: any) {
      notifications.show({
        title: 'Falha ao criar chave',
        message: err?.message || 'Erro desconhecido',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Nova chave de API" size="lg">
      <Stack>
        <TextInput
          label="Tenant"
          description="Identificador do consumidor, ex.: vendax, recomx"
          value={tenantId}
          onChange={(e) => setTenantId(e.currentTarget.value)}
          required
        />
        <TextInput
          label="Produto"
          description="Nome legível, ex.: VendaX.ai"
          value={product}
          onChange={(e) => setProduct(e.currentTarget.value)}
        />
        <Group grow>
          <NumberInput
            label="Requisições por minuto"
            value={requestsPerMinute}
            onChange={(v) => setRequestsPerMinute(Number(v) || 0)}
            min={1}
          />
          <NumberInput
            label="UPs por dia"
            value={upsPerDay}
            onChange={(v) => setUpsPerDay(Number(v) || 0)}
            min={0}
          />
          <NumberInput
            label="UPs por mês"
            description="0 = sem teto"
            value={upsPerMonth}
            onChange={(v) => setUpsPerMonth(Number(v) || 0)}
            min={0}
          />
        </Group>
        <MultiSelect
          label="Features"
          data={AVAILABLE_FEATURES as unknown as string[]}
          value={features}
          onChange={setFeatures}
        />
        <TextInput
          label="Webhook (opcional)"
          description="Callback para jobs assíncronos"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} loading={saving} disabled={!tenantId.trim()}>
            Criar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** A chave em claro aparece uma vez só — o modal precisa deixar isso muito explícito. */
function CreatedKeyModal({ apiKey, onClose }: { apiKey: string | null; onClose: () => void }) {
  return (
    <Modal opened={!!apiKey} onClose={onClose} title="Chave criada" size="lg">
      <Stack>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
          Copie agora. A partir daqui a chave só aparece mascarada, e não há como recuperá-la —
          se perder, o caminho é revogar e criar outra.
        </Alert>
        <Group gap="xs" wrap="nowrap">
          <Code style={{ flex: 1, wordBreak: 'break-all' }}>{apiKey}</Code>
          <CopyButton value={apiKey || ''}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copiada' : 'Copiar'}>
                <ActionIcon color={copied ? 'green' : 'blue'} variant="light" onClick={copy}>
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        <Group justify="flex-end">
          <Button onClick={onClose}>Fechar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
